/**
 * One eHungry order, styled to match OrderCard.jsx and EmailOrderCard.jsx so it
 * sits naturally in the same grid — same card frame (OpsUi OrderCardShell),
 * same header/customer/items/total layout.
 *
 * Two deliberate differences: an "RMF (old)" source tag (this order was taken on the
 * legacy RushMyFood app, not through our checkout) and no Accept / Mark Ready
 * actions. Nobody on this side works these orders — the restaurant already made
 * the food and First Delivery already carried it. The only thing changeable is
 * the status, and that lives in the detail view so it cannot be hit by accident
 * from a grid.
 *
 * eHungry has exactly three statuses, so this is not the marketplace's
 * orderStatus palette with rows deleted — reusing that would imply stages
 * (Accepted, Prepared, Rider Out) that this data never carries.
 */
import React from 'react'
import { Box, Typography } from '@mui/material'
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import { ops, FS, tabular } from './opsTokens'
import {
  OrderCardShell, CardBody, CardHeader, CardFooter, StatusChip, Tag, FulfilmentTag, CustomerLine,
  ItemsLine, Callout, ProviderMark
} from './OpsUi'

/* Tones from the Orders page's one status map (opsTokens.js). */
export const EHUNGRY_STATUS_CONFIG = {
  pending: { label: 'Pending', tone: 'amber' },
  completed: { label: 'Completed', tone: 'emerald' },
  cancelled: { label: 'Cancelled', tone: 'rose' },
}

const TYPE_KIND = { PICKUP: 'pickup', DELIVERY: 'delivery', DINEIN: 'dinein' }

export const EHUNGRY_TYPE_LABEL = {
  PICKUP: 'Pickup',
  DELIVERY: 'Delivery',
  DINEIN: 'Dine-in',
}

/* A ready time this far past when the order was placed means the customer asked
   for it later, not as soon as possible. eHungry has no ASAP flag — its own
   receipt prints "(ASAP)" by reading the same gap. */
export const EHUNGRY_ASAP_WINDOW_MS = 45 * 60 * 1000
/* How long before the food is due the kitchen should pick it up — and, on the
   tablet, when its ticket prints. The two use the same moment so an order joins
   the work list exactly when its paper appears. */
export const EHUNGRY_PRINT_LEAD_MS = 45 * 60 * 1000

export const isScheduledEhungryOrder = (order) =>
  !!order && !!order.readyAt && !!order.placedAt &&
  new Date(order.readyAt).getTime() - new Date(order.placedAt).getTime() > EHUNGRY_ASAP_WINDOW_MS

/** When a pre-order stops being "for later" and becomes work in hand. */
export const ehungryDueAt = (order) =>
  isScheduledEhungryOrder(order)
    ? new Date(order.readyAt).getTime() - EHUNGRY_PRINT_LEAD_MS
    : new Date(order.placedAt).getTime()

const timeAgo = (dateStr) => {
  if (!dateStr) return '—'
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ${mins % 60}m ago`
  return new Date(dateStr).toLocaleDateString()
}

// courierName: what to call the courier when the order doesn't name one — restaurants see RushMyFood, never First Delivery.
const EhungryOrderCard = ({ order, onViewDetail, currencySymbol = '$', courierName = 'First Delivery' }) => {
  const config = EHUNGRY_STATUS_CONFIG[order.status] || { label: order.status, tone: 'neutral' }

  const items = order.items || []
  const isScheduled = isScheduledEhungryOrder(order)
  const itemsSummary = items
    .slice(0, 2)
    .map((i) => `${i.quantity}x ${i.name}${i.variant ? ` [${i.variant}]` : ''}`)
    .join(', ')
  const moreCount = items.length - 2

  return (
    <OrderCardShell
      toneName={config.tone}
      label={`RushMyFood (old) order #${order.remoteId}, ${config.label}`}
      onOpen={() => onViewDetail && onViewDetail(order)}
    >
      <CardBody>
        <CardHeader
          id={`#${order.remoteId}`}
          time={
            <>
              <AccessTimeRoundedIcon sx={{ fontSize: 13 }} />
              <Typography component="span" sx={{ fontSize: FS.xs, color: ops.muted, ...tabular }}>
                {timeAgo(order.placedAt)}
              </Typography>
            </>
          }
          right={<StatusChip toneName={config.tone} label={config.label} />}
        />

        <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap" minWidth={0}>
          <Tag icon={<StorefrontOutlinedIcon />}>RMF (old)</Tag>
          <FulfilmentTag kind={TYPE_KIND[order.orderType]}>
            {EHUNGRY_TYPE_LABEL[order.orderType] || order.orderType || 'Order'}
          </FulfilmentTag>
          {order.restaurantName && (
            <Typography noWrap sx={{ fontSize: FS.sm, color: ops.text2, fontWeight: 500, minWidth: 0 }}>
              {order.restaurantName}
            </Typography>
          )}
        </Box>

        <CustomerLine name={order.customerName} fallback="Customer" />

        {items.length > 0 && (
          <ItemsLine text={itemsSummary} more={moreCount} />
        )}

        {/* A pre-order for later. Visible as soon as it lands, even though the
            kitchen ticket is held until nearer the time. */}
        {isScheduled && (
          <Callout toneName="slate" icon={<CalendarMonthOutlinedIcon />}>
            For later — {new Date(order.readyAt).toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
          </Callout>
        )}
      </CardBody>

      <CardFooter
        left={
          <Typography sx={{ fontSize: FS.xs, color: ops.muted, ...tabular }}>
            {items.length} item{items.length === 1 ? '' : 's'}
          </Typography>
        }
        total={`${currencySymbol}${Number(order.total || 0).toFixed(2)}`}
      >
        {/* Who actually carried it, when First Delivery did. A pickup has no such
            line at all rather than an empty one implying a driver that never was. */}
        {order.courier && (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0,
            px: 1, py: 0.75, borderRadius: '8px', bgcolor: ops.subtle, border: `1px solid ${ops.border}`
          }}>
            <ProviderMark name={order.courier.provider || courierName} />
            <Typography noWrap sx={{ fontSize: FS.xs, color: ops.text2, fontWeight: 500, minWidth: 0 }}>
              {order.courier.provider || courierName}
              {order.courier.status ? ` · ${order.courier.status.replace(/_/g, ' ')}` : ''}
            </Typography>
          </Box>
        )}
      </CardFooter>
    </OrderCardShell>
  )
}

export default EhungryOrderCard
